
import { Button, CircularProgress } from "@mui/material";
import "./choose-language.page.scss";
import "./public-interview.page.scss";
import FLAG_ENG from "../assets/images/flag-eng.png";
import FLAG_TH from "../assets/images/flag-th.png";
import { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck, faRotateRight, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../providers/language.provider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LOGO_SO from "../assets/images/logo-so-black.png";
import LOGO_PROFI from "../assets/images/logo-profi.svg";
import { trackInviteEmailLinkOpen, verifyPublicCandidate } from "../services/interview-api.services";
import config from "../config";
import { trackEvent } from "../utils/trackGA4.util";
import { GAConfig } from "../utils/ga-config.util";
import UtilityService from "../utils/ga4.util";
import axios from "axios";
import APIService from "../services/api.services";
import Lottie from "lottie-react";
import LOADING_BLUE from "../assets/json/loading-blue.json";
import LOADING_GRADIENT from "../assets/json/loading-gradient.json";

const VOICE_CONFIG = [
    { value: "M1", name: "Alex",   gender: "male",   bg: "linear-gradient(135deg,#667eea,#764ba2)" },
    { value: "M2", name: "Marcus", gender: "male",   bg: "linear-gradient(135deg,#4facfe,#00f2fe)" },
    { value: "F1", name: "Aria",   gender: "female", bg: "linear-gradient(135deg,#f093fb,#f5576c)" },
    { value: "F2", name: "Emma",   gender: "female", bg: "linear-gradient(135deg,#fccb90,#d57eeb)" },
] as const;

const INACTIVE_WIDTH = 130;
const ACTIVE_WIDTH = 200;


const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const PublicInterviewPage = () => {
    const { company_id, job_question_id, candidate_id } = useParams<{ company_id: string; job_question_id: string; candidate_id: string }>();
    const { translations, switchLanguage } = useLanguage();

    const [isTh, setIsTh] = useState(false);
    const [isEn, setIsEn] = useState(false);
    const [voiceDisplayIndex, setVoiceDisplayIndex] = useState<number>(() => {
        const saved = localStorage.getItem("interview_voice_selection");
        const idx = VOICE_CONFIG.findIndex((v) => v.value === saved);
        return idx >= 0 ? idx : 0;
    });
    const [isPlayingSample, setIsPlayingSample] = useState(false);
    const [carouselWidth, setCarouselWidth] = useState(0);

    const navigate = useNavigate();
    const query = useQuery();
    const [appId, setAppId] = useState<string | null>("");
    const [templateId, setTemplateId] = useState<string | null>("");
    const [isAutoNavigating, setIsAutoNavigating] = useState(false);
    const hasCheckedRef = useRef(false);
    const inviteLinkTrackedRef = useRef(false);
    const previewAudioRef = useRef<HTMLAudioElement | null>(null);
    const carouselWrapRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!company_id || !job_question_id || !candidate_id || inviteLinkTrackedRef.current) return;
        inviteLinkTrackedRef.current = true;
        trackInviteEmailLinkOpen({
            company_id,
            job_question_id,
            candidate_id,
        });
    }, [company_id, job_question_id, candidate_id]);

    useEffect(() => {
        try {
            const inIframe = window.self !== window.top;
            if (inIframe && document.referrer) {
                const refHost = new URL(document.referrer).host;
                if (refHost === "english-interview-test.web.app" || refHost === "speakout.scoutout.co") {
                    setAppId("ENGLISH_INTERVIEW_AI");
                    return;
                }
            }
        } catch (e) {}

        if (query.get("app_id")) {
            setAppId(query.get("app_id"));
        } else {
            setAppId("SCOUTOUT");
        }
        if (query.get("template_id")) {
            setTemplateId(query.get("template_id"));
        }
    }, [query]);

    useEffect(() => {
        const checkByPassLanguage = async () => {
            if (!company_id || !job_question_id || !candidate_id || hasCheckedRef.current) return;
            setIsAutoNavigating(true);
            try {
                const response = await axios
                    .get(config.report_api.url + "/settings/public/by-pass-choose-language/" + company_id, {
                        headers: { "Content-Type": "application/json" },
                    })
                    .catch(() => null);

                if (response?.data?.is_success && response?.data?.data) {
                    const { en = [], th = [] } = response.data.data;

                    const doNavigate = (res: { is_success: boolean; data?: { email: string } }) => {
                        if (!res?.is_success) return;
                        trackEvent(GAConfig.action.verifyRoom, UtilityService());
                        const emailSeg = encodeURIComponent(
                            res.data!.email || `public.${candidate_id}@interview.ai`
                        );
                        const base = "/" + job_question_id + "/" + candidate_id + "/" + emailSeg + "/interview-room";
                        const q = appId === "INTERVIEW_AI" ? "?app_id=" + appId + "&template_id=" + templateId : "?app_id=" + appId;
                        navigate(base + q);
                    };

                    const handleErr = (error: any) => {
                        setIsAutoNavigating(false);
                        const msg = error?.response?.data?.error;
                        if (msg === "This candidate already interview") alert("ผู้สมัครเคยสัมภาษณ์ไปแล้ว");
                        else if (msg === "Email does not match for the provided candidate_id") alert("อีเมล ผู้สมัครไม่ตรงในระบบ");
                        else if (msg === "candidate_id not found in candidates list") alert("ไม่พบรายชื่อผู้สมัครในระบบ");
                        else if (msg === "Interview period has ended") alert("ลิงก์สัมภาษณ์หมดอายุแล้ว");
                        else if (msg === "Interview period has not started yet") alert("ยังไม่ถึงช่วงเวลาที่เปิดสัมภาษณ์");
                        if (appId === "PROFI") notifyParentToRedirect();
                    };

                    if (en.includes(job_question_id)) {
                        hasCheckedRef.current = true;
                        setIsEn(true);
                        setIsTh(false);
                        switchLanguage("en");
                        verifyPublicCandidate(company_id, job_question_id, candidate_id).then(doNavigate).catch(handleErr);
                        return;
                    }
                    if (th.includes(job_question_id)) {
                        hasCheckedRef.current = true;
                        setIsTh(true);
                        setIsEn(false);
                        switchLanguage("th");
                        verifyPublicCandidate(company_id, job_question_id, candidate_id).then(doNavigate).catch(handleErr);
                        return;
                    }
                }
                setIsAutoNavigating(false);
            } catch (error) {
                console.error("Error checking by-pass-choose-language:", error);
                setIsAutoNavigating(false);
            } finally {
                hasCheckedRef.current = true;
            }
        };
        checkByPassLanguage();
    }, [company_id, job_question_id, candidate_id, navigate, switchLanguage, appId, templateId]);

    const notifyParentToRedirect = () => {
        window.parent.postMessage({ type: "REDIRECT", payload: { url: config.partner.profi } }, "*");
    };

    const handleClickLang = (field: string) => {
        if (isAutoNavigating) return;
        if (field === "en") {
            setIsTh(false);
            setIsEn(true);
            switchLanguage("en");
        } else {
            setIsTh(true);
            setIsEn(false);
            switchLanguage("th");
        }
        trackEvent(GAConfig.action.changeLanguage, UtilityService());
    };

    const stopPreviewAudio = () => {
        if (previewAudioRef.current) {
            previewAudioRef.current.pause();
            previewAudioRef.current = null;
        }
        setIsPlayingSample(false);
    };

    const handleSelectVoice = (idx: number) => {
        stopPreviewAudio();
        setVoiceDisplayIndex(idx);
    };

    const handleNavigateVoice = (dir: number) => {
        const next = (voiceDisplayIndex + dir + VOICE_CONFIG.length) % VOICE_CONFIG.length;
        handleSelectVoice(next);
    };

    useEffect(() => {
        const syncCarouselWidth = () => setCarouselWidth(carouselWrapRef.current?.clientWidth || 0);
        syncCarouselWidth();
        window.addEventListener("resize", syncCarouselWidth);
        return () => window.removeEventListener("resize", syncCarouselWidth);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const cv = VOICE_CONFIG[voiceDisplayIndex];
        const sampleText =
            cv.gender === "male"
                ? (translations.voice_sample_text_male || "Hello, I'll be your interviewer today.")
                : (translations.voice_sample_text_female || "Hello, I'll be your interviewer today.");

        const play = async () => {
            setIsPlayingSample(true);
            try {
                const res = await APIService().getTTS({ input: sampleText, voice: cv.value, speed: "X1" });
                if (cancelled) {
                    setIsPlayingSample(false);
                    return;
                }
                if (res?.data) {
                    const blob = new Blob([new Uint8Array(res.data)], { type: "audio/mpeg" });
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    previewAudioRef.current = audio;
                    audio.onended = () => {
                        setIsPlayingSample(false);
                        URL.revokeObjectURL(url);
                        previewAudioRef.current = null;
                    };
                    audio.onerror = () => {
                        setIsPlayingSample(false);
                        URL.revokeObjectURL(url);
                        previewAudioRef.current = null;
                    };
                    await audio.play();
                } else {
                    setIsPlayingSample(false);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error("Error playing voice sample:", err);
                    setIsPlayingSample(false);
                }
            }
        };
        play();
        return () => {
            cancelled = true;
            stopPreviewAudio();
        };
    }, [voiceDisplayIndex, translations.voice_sample_text_male, translations.voice_sample_text_female]);

    const handlePlaySample = async () => {
        if (isPlayingSample) return;
        const cv = VOICE_CONFIG[voiceDisplayIndex];
        const sampleText =
            cv.gender === "male"
                ? (translations.voice_sample_text_male || "Hello, I'll be your interviewer today.")
                : (translations.voice_sample_text_female || "Hello, I'll be your interviewer today.");
        setIsPlayingSample(true);
        try {
            stopPreviewAudio();
            const res = await APIService().getTTS({ input: sampleText, voice: cv.value, speed: "X1" });
            if (res?.data) {
                const blob = new Blob([new Uint8Array(res.data)], { type: "audio/mpeg" });
                const url = URL.createObjectURL(blob);
                const audio = new Audio(url);
                previewAudioRef.current = audio;
                audio.onended = () => {
                    setIsPlayingSample(false);
                    URL.revokeObjectURL(url);
                    previewAudioRef.current = null;
                };
                audio.onerror = () => {
                    setIsPlayingSample(false);
                    URL.revokeObjectURL(url);
                    previewAudioRef.current = null;
                };
                await audio.play();
            } else {
                setIsPlayingSample(false);
            }
        } catch (err) {
            console.error("Error playing voice sample:", err);
            setIsPlayingSample(false);
        }
    };

    const submit = () => {
        if (isAutoNavigating || (!isEn && !isTh)) return;
        localStorage.setItem("interview_voice_selection", VOICE_CONFIG[voiceDisplayIndex].value);
        stopPreviewAudio();

        verifyPublicCandidate(company_id, job_question_id, candidate_id)
            .then((res) => {
                if (res.is_success) {
                    trackEvent(GAConfig.action.verifyRoom, UtilityService());
                    const emailSeg = encodeURIComponent(
                        res.data!.email || `public.${candidate_id}@interview.ai`
                    );
                    const base = "/" + job_question_id + "/" + candidate_id + "/" + emailSeg + "/interview-room";
                    const q = appId === "INTERVIEW_AI" ? "?app_id=" + appId + "&template_id=" + templateId : "?app_id=" + appId;
                    navigate(base + q);
                }
            })
            .catch((error: any) => {
                const msg = error?.response?.data?.error;
                if (msg === "This candidate already interview") alert("ผู้สมัครเคยสัมภาษณ์ไปแล้ว");
                else if (msg === "Email does not match for the provided candidate_id") alert("อีเมล ผู้สมัครไม่ตรงในระบบ");
                else if (msg === "candidate_id not found in candidates list") alert("ไม่พบรายชื่อผู้สมัครในระบบ");
                else if (msg === "Interview period has ended") alert("ลิงก์สัมภาษณ์หมดอายุแล้ว");
                else if (msg === "Interview period has not started yet") alert("ยังไม่ถึงช่วงเวลาที่เปิดสัมภาษณ์");
                if (appId === "PROFI") notifyParentToRedirect();
            });
    };

    const cv = VOICE_CONFIG[voiceDisplayIndex];
    const lottieData = cv.gender === "male" ? LOADING_BLUE : LOADING_GRADIENT;
    const voiceGenderLabel =
        cv.gender === "male"
            ? (translations.voice_gender_male || "Male")
            : (translations.voice_gender_female || "Female");
    const activeCenterFromTrackStart = voiceDisplayIndex * INACTIVE_WIDTH + ACTIVE_WIDTH / 2;
    const trackOffset = carouselWidth / 2 - activeCenterFromTrackStart;

    return (
        <>
            {isAutoNavigating ? (
                <div className="pi-loading-wrap">
                    <CircularProgress className="pi-loading-spinner" />
                    <span className="pi-loading-text">กำลังเตรียมการสัมภาษณ์...</span>
                </div>
            ) : (
                <div className="cl-page">
                    <div className="cl-header">
                        {appId === "PROFI" ? (
                            <img src={LOGO_PROFI} className="cl-logo pi-logo-profi" alt="logo" />
                        ) : (
                            <img src={LOGO_SO} className="cl-logo" alt="logo" />
                        )}
                    </div>
                    <div className="cl-content">
                        <div className="cl-content-spacer" aria-hidden="true" />
                        <div className="cl-content-inner">
                            <div className="cl-step-header">
                                <span className="cl-step-badge">1</span>
                                <span className="cl-step-title">{translations.choose_language}</span>
                            </div>
                            <div className="cl-lang-cards">
                                <div
                                    className={`cl-lang-card${isEn ? " active" : ""}`}
                                    onClick={() => handleClickLang("en")}
                                    role="button"
                                    aria-pressed={isEn}
                                >
                                    <img src={FLAG_ENG} alt="EN" className="cl-lang-flag" />
                                    <span className="cl-lang-name">English</span>
                                    {isEn && <FontAwesomeIcon icon={faCheck} className="cl-lang-check" />}
                                </div>
                                <div
                                    className={`cl-lang-card${isTh ? " active" : ""}`}
                                    onClick={() => handleClickLang("th")}
                                    role="button"
                                    aria-pressed={isTh}
                                >
                                    <img src={FLAG_TH} alt="TH" className="cl-lang-flag" />
                                    <span className="cl-lang-name">ภาษาไทย</span>
                                    {isTh && <FontAwesomeIcon icon={faCheck} className="cl-lang-check" />}
                                </div>
                            </div>

                            <div className="cl-section-divider" />

                            <div className="cl-step-header">
                                <span className="cl-step-badge secondary">2</span>
                                <span className="cl-step-title">{translations.voice_modal_title}</span>
                            </div>
                            <div className="cl-carousel-wrap" ref={carouselWrapRef}>
                                <button type="button" className="cl-carousel-arrow left" onClick={() => handleNavigateVoice(-1)} aria-label="previous voice">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                <div className="cl-carousel-track" style={{ transform: `translateX(${trackOffset}px)` }}>
                                    {VOICE_CONFIG.map((v, i) => {
                                        const isActive = i === voiceDisplayIndex;
                                        return (
                                            <div
                                                key={v.value}
                                                className={`cl-carousel-item${isActive ? " active" : ""}`}
                                                onClick={() => handleSelectVoice(i)}
                                            >
                                                {isActive ? (
                                                    <div className="cl-ring-outer">
                                                        <div className="cl-ring-middle">
                                                            <div className="cl-ring-inner">
                                                                <Lottie animationData={lottieData} loop className="cl-lottie" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="cl-inactive-circle" style={{ background: v.bg }}>
                                                        <span className="cl-inactive-initial">{v.name.charAt(0)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <button type="button" className="cl-carousel-arrow right" onClick={() => handleNavigateVoice(1)} aria-label="next voice">
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                            <div className="cl-voice-info">
                                <div className="cl-voice-name-row">
                                    <span className="cl-voice-name">{cv.name}</span>
                                    {isPlayingSample ? (
                                        <span className="cl-voice-playing-dot" />
                                    ) : (
                                        <button type="button" className="cl-replay-btn" onClick={handlePlaySample} aria-label="replay">
                                            <FontAwesomeIcon icon={faRotateRight} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="cl-stats-row">
                                <div className="cl-stat">
                                    <span className="cl-stat-label">{translations.voice_stat_voice || "Voice"}</span>
                                    <span className="cl-stat-value">
                                        {voiceDisplayIndex + 1} / {VOICE_CONFIG.length}
                                    </span>
                                </div>
                                <div className="cl-stat-divider" />
                                <div className="cl-stat">
                                    <span className="cl-stat-label">{translations.voice_stat_gender || "Gender"}</span>
                                    <span className="cl-stat-value" data-gender={cv.gender}>
                                        {voiceGenderLabel}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="cl-content-spacer" aria-hidden="true" />
                    </div>
                    <Button className="cl-submit-btn" onClick={submit}>
                        {translations.next_button}
                        <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
                    </Button>
                </div>
            )}
        </>
    );
};

export default PublicInterviewPage;
